import { useState, useCallback } from 'react';
import { leadService } from '../services/domain/leadService';
import type { ImportRow } from '../types/import';

interface CreateLeadResult {
  success: boolean;
  message?: string;
  error?: string;
}

interface UseImportsResult {
  data: ImportRow[];
  error: string | null;
  creating: boolean;
  createMassiveLead: (formData: ImportRow[]) => Promise<CreateLeadResult>;
  clearError: () => void;
}

export const useImports = (): UseImportsResult => {
  const [data] = useState<ImportRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const createMassiveLead = useCallback(async (
    formData: ImportRow[]
  ): Promise<CreateLeadResult> => {
    // Validación previa
    if (!formData || formData.length === 0) {
      const errorMessage = 'No hay datos para importar';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    }

    try {
      setCreating(true);
      setError(null); // Limpiar error previo

      const result = await leadService.createLeadsMass(formData);
      
      if (result.error) {
        const errorMessage = `Error al crear leads: ${result.error}`;
        setError(errorMessage);
        return {
          success: false,
          error: errorMessage
        };
      }
      return {
        success: true,
        message: result.message || 'Leads creados exitosamente'
      };

    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Error desconocido al crear leads';
      
      setError(errorMessage);
      
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setCreating(false);
    }
  }, []);

  return {
    data,
    error,
    creating,
    createMassiveLead,
    clearError
  };
};